import { DateTime } from "luxon"
import { analyses, samples, sampleView, message, limsRelease, limsReruns, limsResults, limsOos } from "state_management"
import AddEditSamplePopup from "./AddEditSamplePopup"
import ESignatureModal from "components/Lims/ESignatureModal"
import CertificateOfAnalysis from "components/Lims/CertificateOfAnalysis"
import { useDispatch, useSelector } from "react-redux"
import { useClosePopup } from "components/Popup/useClosePopup"
import { useState, useLayoutEffect, useEffect, useMemo } from "react"
import { Popup } from "components"

import { chooseOptimalColor } from "utils/scripts/schedule"

import { FiCheck, FiFlag } from "react-icons/fi"
import { TbSquareLetterM } from "react-icons/tb"
import { BsCalendar2 } from "react-icons/bs"
import { AiOutlineExclamation } from "react-icons/ai"
import { HiOutlineArrowNarrowDown } from "react-icons/hi"
import { FaCircle } from "react-icons/fa"
import { FiCircle, FiAlertTriangle, FiFileText, FiSlash, FiCheckCircle, FiAlertCircle, FiUploadCloud, FiRefreshCw, FiRotateCcw } from "react-icons/fi"
import useT from "utils/hooks/useT"


export default function SampleRowElement({
  group,
  activeSample,
  setActiveSample,
  samplePriority: _samplePriority,
  setSamplePriority,
  filteredSampleGroups,
  setActiveSampleAnalysis,
  setActiveOosId,
  rowId,
  isReadOnly = false,
}) {

  const dispatch = useDispatch()
  const { t } = useT("common")
  const [groupPriority, setGroupPriority] = useState(group.maxPriority)
  const timeZone = useSelector((state) => state.teamInfo.timezone)

  // Hook for a RefObject that checks if popup should close (only on outside click for priority dropdown)
  let domNode = useClosePopup(() => {
    setActiveSample(null)
  }, { closeOnEsc: false, closeOnOutsideClick: true })

  // Measure dropdown position when opened and flip it upward if it would overflow the viewport
  const [dropUp, setDropUp] = useState(false)

  useLayoutEffect(() => {
    if (!domNode || !domNode.current) return
    // Only run when this group's dropdown is active
    if (activeSample !== rowId) {
      setDropUp(false)
      return
    }
    const node = domNode.current

    const trigger = node.parentElement ? node.parentElement.getBoundingClientRect() : node.getBoundingClientRect()
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight

    const HEADER_HEIGHT = 36
    const ITEM_HEIGHT = 38
    const ITEM_COUNT = 3
    const GAP = 8

    const contentHeight = HEADER_HEIGHT + ITEM_COUNT * ITEM_HEIGHT
    const spaceBelow = viewportHeight - trigger.bottom
    const shouldDropUp = spaceBelow < contentHeight + GAP
    if (shouldDropUp !== dropUp) setDropUp(shouldDropUp)
  }, [activeSample, rowId, domNode, dropUp])

  const prioNumberMeaning = {1: t("priorityHigh"), 2: t("priorityMedium"), 3: t("priorityLow")}

  // Show edit popup for manual sample groups
  const [showEditPopup, setShowEditPopup] = useState(false)
  const [selectedSampleForEdit, setSelectedSampleForEdit] = useState(null)

  // Select raw slices, then memoize derived collections so the selector returns
  // stable references (prevents unnecessary rerenders / selector warnings).
  const analysisDefs = useSelector((state) => state.analysisDefinitions)
  const sampleGroupsFromState = useSelector((state) => state.sampleView.sampleGroups)

  // LIMS F-C4: release status for this sample row (lot + observation id). A string, so the
  // selector stays referentially stable across renders.
  const showLims = useSelector((state) => state.teamStatus.showLims)
  // SPEC-044: every LIMS selector below is explicitly short-circuited on showLims (returning the
  // empty/false default for non-LIMS teams) — the decoupling is by explicit gate, not by the lims
  // slices happening to be empty. Mirrors the openInvestigationId / releaseExcludedIds guards below.
  const releaseStatus = useSelector((state) => (showLims ? limsRelease.selectSampleReleaseStatus(state, group) : ""))
  const rejectedSampleIds = useSelector(
    (state) => {
      if (!showLims) return ""
      const ids = (group.analyses || [])
        .map((a) => a.sampleId)
        .filter((id) => id && limsRelease.selectIsSampleBatchRejected(state, id))
      return ids.join(",")
    }
  )
  // SPEC-012: the sample-analyses of this row whose QC result is QA-approved (the terminal,
  // release-eligible QA disposition), so a "QA-approved" icon is shown on the exact sample-analysis
  // card. (On the sample number the QA disposition now rolls into the lifecycle-status icon — K24.)
  // A comma-joined string keeps the selector referentially stable across renders.
  const qaApprovedSampleIds = useSelector(
    (state) => {
      if (!showLims) return ""
      const ids = (group.analyses || [])
        .map((a) => a.sampleId)
        .filter((id) => id && limsResults.selectIsSampleQaApproved(state, id))
      return ids.join(",")
    }
  )
  // SPEC-010: whether a RERUN was auto-created (on a QA may-reanalyse signature) for any of this
  // row's samples — surfaced as a read-only "RERUN created" hint. SPEC-022 K16: also resolve the
  // FORWARD link's target sample number (for the tooltip) and the REVERSE link (this row IS a
  // rerun of an original), so rerun traceability reads BOTH ways. A comma-joined string keeps the
  // selector referentially stable across renders.
  const rerunForwardSample = useSelector((state) => {
    if (!showLims) return ""
    for (const a of group.analyses || []) {
      if (!a.sampleId) continue
      const link = limsReruns.selectRerunForOriginal(state, a.sampleId)
      if (link) return link.sampleNumber || ""
    }
    return ""
  })
  const rerunOriginalSample = useSelector((state) => {
    if (!showLims) return ""
    for (const a of group.analyses || []) {
      if (!a.sampleId) continue
      const link = limsReruns.selectRerunOriginalFor(state, a.sampleId)
      if (link) return link.sampleNumber || ""
    }
    return ""
  })
  const hasRerun = useSelector((state) =>
    showLims && (group.analyses || []).some((a) => a.sampleId && limsReruns.selectRerunForOriginal(state, a.sampleId))
  )
  const isRerun = useSelector((state) =>
    showLims && (group.analyses || []).some((a) => a.sampleId && limsReruns.selectRerunOriginalFor(state, a.sampleId))
  )
  // SPEC-018: the id of an OPEN OOS investigation on any of this row's samples (or "" when none), so
  // an OOS badge is shown on the sample number; clicking it opens the investigation modal. A string
  // keeps the selector referentially stable across renders.
  const openInvestigationId = useSelector((state) => {
    if (!showLims) return ""
    for (const a of group.analyses || []) {
      if (!a.sampleId) continue
      const open = limsOos.selectOpenInvestigationsForSample(state, a.sampleId)
      if (open.length > 0) return open[0].id
    }
    return ""
  })
  // SPEC-020 four-eyes on release: everyone who entered / reviewed / override-signed one of this
  // group's QC results may not also sign its release. Comma-joined string so the selector stays
  // referentially stable across renders (same pattern as the id sets above).
  const releaseExcludedIds = useSelector((state) =>
    showLims ? limsRelease.selectReleaseExclusions(state, group).join(",") : ""
  )
  const [showReleaseModal, setShowReleaseModal] = useState(false)
  const [showCoa, setShowCoa] = useState(false)

  const handleRelease = (signature) => {
    setShowReleaseModal(false)
    dispatch(limsRelease.release(group, signature))
      .then(() => dispatch(message.success(t("limsReleaseDone"))))
      .catch((err) => {
        // SPEC-020: release() revalidates the gate at signing time — a no-longer-READY lot gets
        // its own message (nothing was signed/persisted) instead of the generic failure. #4105:
        // that message now names WHY (the outstanding analyses, or the hold that blocked).
        if (!limsRelease.isReleaseNotReadyError(err)) {
          dispatch(message.error(t("limsReleaseFailed")))
          return
        }
        const { key, params } = limsRelease.releaseNotReadyMessage(err)
        dispatch(message.error(t(key, params)))
      })
  }

  const analysisCollection = useMemo(() => {
    if (!analysisDefs) return []
    return Object.values(analysisDefs).map((a) => ({
      id: a.analysisId || a.id || a.name,
      name: a.display_name || a.displayName || a.name,
    }))
  }, [analysisDefs])

  const productCollection = useMemo(() => {
    const set = new Set()
    ;(sampleGroupsFromState || []).forEach((s) => set.add(s.productName || ""))
    return Array.from(set).map((p) => ({ id: p, name: p === "" ? t("samplesWithoutProduct") : p }))
  }, [sampleGroupsFromState, t])

  // Keep local priority in sync with prop updates (so when store updates, all rows update)
  useEffect(() => {
    if (group.maxPriority !== groupPriority) setGroupPriority(group.maxPriority)
  }, [group.maxPriority, groupPriority])

  // Memoize the selected sample to prevent it from changing on every render
  const selected = useMemo(() => {
    return selectedSampleForEdit || {}
  }, [selectedSampleForEdit])

  // The set of this group's sample-analysis ids whose batch/lot was rejected (SPEC-010), derived
  // from the stable comma-joined string so the set identity is steady across renders.
  const rejectedSet = useMemo(
    () => new Set(rejectedSampleIds ? rejectedSampleIds.split(",").filter(Boolean) : []),
    [rejectedSampleIds]
  )

  // SPEC-012: the set of this group's QA-approved sample-analysis ids (steady identity across
  // renders), used by the per-analysis "QA-approved" card icon.
  const qaApprovedSet = useMemo(
    () => new Set(qaApprovedSampleIds ? qaApprovedSampleIds.split(",").filter(Boolean) : []),
    [qaApprovedSampleIds]
  )

  // Compute initial values for the popup from the selected sample (with fallbacks to group)
  const initialSampleName = selected.sampleName || selected.sample_name || group.sampleName || ""
  const initialObservationId = selected.observationId || selected.observation_id || group.observationId
  const initialArrival = selected.arrival || group.arrival || group.maxArrival || ""
  const initialDeadline = selected.deadline || group.deadline || group.minDeadline || ""
  const initialPriority = selected.priority || selected.maxPriority || group.maxPriority || 2
  const initialStatus = selected.status || group.status || "TODO"
  const initialProductName = selected.productName || group.productName || ""
  const initialCustomerName = selected.customerName || selected.customer_name || group.customerName || group.customer_name || ""
  const initialExclusive = typeof selected.exclusive !== 'undefined' ? selected.exclusive : group.exclusive
  const initialIsFlagged = typeof selected.isFlagged !== 'undefined' ? selected.isFlagged : group.isFlagged

  // Get existing analyses on the sample to pass for filtering
  const initialAnalysisIds = useMemo(() => {
    if (!selected || !group.analyses) return []
    
    // Get all analyses from the group that match this sample's observationId
    const sampleObservationId = selected.observationId || selected.observation_id
    const sampleAnalyses = group.analyses.filter(a => 
      String(a.observationId || a.observation_id) === String(sampleObservationId)
    )
    
    // Map to analysis IDs
    return sampleAnalyses.map(a => {
      // Try to find the matching analysis in analysisCollection to get the canonical ID
      const foundByName = analysisCollection.find(ac => String(ac.name) === String(a.name || a.analysis_name))
      if (foundByName) return foundByName.id
      
      const foundById = analysisCollection.find(ac => String(ac.id) === String(a.analysisId || a.analysis_id || a.id))
      if (foundById) return foundById.id
      
      return a.analysisId || a.analysis_id || a.id
    }).filter(Boolean)
  }, [selected, group.analyses, analysisCollection])

  return (
    <div className="samplegroup-row" key={group.sampleName}>
      <h3
        title={`${group.sampleName}\n${t("sampleIdWithValue", { id: group.observationId })}${initialCustomerName ? `\n${t("customerName")}: ${initialCustomerName}` : ""}`}
        onClick={() => {
          if (isReadOnly) return
          // Determine candidate sample objects (some backends use group.samples, older code used group.analyses)
          const candidates = Array.isArray(group.samples) && group.samples.length > 0 ? group.samples : (group.analyses || [])

          // Try to find a candidate matching this observation id
          const matching = candidates.find((s) => String(s.observationId || s.observation_id) === String(group.observationId))

          // Open edit popup only if the targeted sample is manual
          const targetSampleForEdit = matching || candidates.find((s) => s.manual === true) || null
          if (targetSampleForEdit && targetSampleForEdit.manual === true) {
            setSelectedSampleForEdit(targetSampleForEdit)
            setShowEditPopup(true)
            return
          }
          setActiveSample(activeSample === rowId ? null : rowId)
          const baseName = group.baseSampleName || group.sampleName
          const found = filteredSampleGroups.find((obj) => obj.sampleName === baseName)
          if (found) setSamplePriority(found.maxPriority)
        }}
      >
        {(() => {
          const candidates = Array.isArray(group.samples) && group.samples.length > 0 ? group.samples : (group.analyses || [])
          const hasManual = candidates.find((s) => s && (s.manual === true || String(s.manual) === 'true'))
          if (hasManual) {
            return (
              <div className="manual-sample-badge" title={t("manualSample")}>
                <TbSquareLetterM />
              </div>
            )
          }
          return null
        })()}

        <span className="sampleDeadline">
          {DateTime.fromISO(group.minDeadline, { zone: timeZone }).toFormat("dd/MM")}
        </span>
        <span className="sampleName">{group.sampleName}</span>
        {/* Sample-card icons. Left group = LIMS status (status / OOS / done / rerun), packed tight
            with a small gap (empty cells collapse). RR2: priority + the sample-warning triangle form
            a SEPARATE group (.samplerow-prio-warning) set a bit apart on the right. */}
        <span className="sampleIcons">
          {/* Slot 1 - release/QA lifecycle status (showLims only). One icon, coloured + tooltipped
              per status; READY and RELEASED carry the release / certificate actions. */}
          <span className="icon-slot icon-slot-status">
            {showLims && (() => {
              const RS = limsRelease.RELEASE_STATUS
              const statusTitle = t(releaseLabelKey(releaseStatus))
              switch (releaseStatus) {
                case RS.RELEASED:
                  return (
                    <FiFileText
                      className="samplerow-status-icon is-released samplerow-cert-icon"
                      title={`${statusTitle} - ${t("limsCoaButton")}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowCoa(true)
                      }}
                    />
                  )
                case RS.READY:
                  return (
                    <FiUploadCloud
                      className="samplerow-status-icon is-ready samplerow-release-icon-btn"
                      title={`${statusTitle} - ${t("limsReleaseAction")}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowReleaseModal(true)
                      }}
                    />
                  )
                case RS.BATCH_REJECTED:
                  return <FiSlash className="samplerow-status-icon is-batch-rejected" title={statusTitle} />
                case RS.ON_HOLD:
                  return <FiAlertCircle className="samplerow-status-icon is-on-hold" title={statusTitle} />
                case RS.IN_REVIEW:
                  return <FiCheckCircle className="samplerow-status-icon is-in-review" title={statusTitle} />
                default:
                  // RR2: no icon for the plain "in progress" state — it showed on EVERY sample
                  // (planned, unplanned, values or not) and carried no signal. The slot collapses.
                  return null
              }
            })()}
          </span>
          {/* OOS-flag: an OPEN OOS investigation (clickable, opens the modal). */}
          <span className="icon-slot icon-slot-oos">
            {showLims && openInvestigationId && (
              <FiAlertCircle
                className="samplerow-oos-icon"
                title={t("limsOosBadge")}
                onClick={(e) => {
                  e.stopPropagation()
                  if (setActiveOosId) setActiveOosId(openInvestigationId)
                }}
              />
            )}
          </span>
          {/* Slot 4 - sample done. */}
          <span className="icon-slot icon-slot-done">
            {group.isDone && <FiCheck title={t("sampleIsDone")} />}
          </span>
          {/* Slot 5 - rerun traceability, BOTH ways (K16): on an ORIGINAL whose rerun was created,
              the forward icon's tooltip names the new sample; on the RERUN row itself, a distinct
              marker names the original. (Read-only — replaces the old text hint.) */}
          <span className="icon-slot icon-slot-rerun">
            {showLims && hasRerun && (
              <FiRefreshCw
                className="samplerow-rerun-icon"
                title={rerunForwardSample ? t("limsRerunCreatedToBadge", { sample: rerunForwardSample }) : t("limsRerunHint")}
              />
            )}
            {showLims && isRerun && (
              <FiRotateCcw
                className="samplerow-rerun-of-icon"
                title={t("limsRerunOfBadge", { sample: rerunOriginalSample })}
              />
            )}
          </span>
        </span>
        {/* RR2: priority + the sample-warning triangle, grouped tight together and set a bit apart
            from the LIMS status icons (manual-test feedback). Collapses when neither is present. */}
        <span className="samplerow-prio-warning">
          {groupPriority === 1 && <AiOutlineExclamation title={t("sampleHighestPriority")} />}
          {groupPriority === 3 && <HiOutlineArrowNarrowDown title={t("sampleLowestPriority")} />}
          {group.analyses && group.analyses.some(analysis => analysis.shouldBeDone) && (<FiAlertTriangle className="alert-triangle-sample" title={t("sampleInProgressOnDoneAnalysis")} />)}
        </span>
        {activeSample === rowId && (
          <div className={`sampleview-dropdown ${dropUp ? 'dropup' : ''}`} ref={domNode}>
            <div className="sampleview-dropdown-content">
              <header className="sampleview-dropdown-header">{t("priority")}</header>
              {[1, 2, 3].map((priority) => {
                return (
                  <div
                    key={priority}
                    className="sampleview-dropdown-item"
                    onClick={() => {
                      if (group.maxPriority !== priority) {
                        setGroupPriority(priority)
                        // Use the base sample name when dispatching priority change
                        const baseName = group.baseSampleName || group.sampleName
                        const observationId = group.observationId
                        const previous = group.maxPriority
                        // Optimistically update the store so all rows update immediately
                        dispatch({ type: "samples/changePriority/OPTIMISTIC", args: [priority, baseName, observationId] })
                        dispatch(samples.changePriority(priority, baseName, observationId)).then (() => {
                          // refresh unplanned analyses; actual store already updated optimistically
                          dispatch(analyses.fetchUnplanned())
                        }).catch(() => {
                          // Revert optimistic update if API fails
                          dispatch({ type: "samples/changePriority/REVERT", args: [previous, baseName, observationId] })
                        })
                      }
                    }}
                  >
                    {priority + " " + prioNumberMeaning[priority]}{" "}
                    {groupPriority === priority ? (
                      <FaCircle style={{ width: "0.75rem", height: "0.75rem" }} />
                    ) : (
                      <FiCircle
                        style={{
                          width: "0.75rem",
                          height: "0.75rem",
                          color: "white",
                          backgroundColor: "white",
                          borderRadius: "50%",
                        }}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </h3>

      <div className="samplerow-analyses-grouped">
        {group.analyses.map((analysis, idx) => (
          <div
            className="samplerow-analysis"
            key={idx}
            style={{ backgroundColor: analysis.color, color: chooseOptimalColor(analysis.color), justifyContent: analysis.shouldBeDone ? 'space-between' : ''}}
            title={analysis.name + "\n" + t("sampleIdWithValue", { id: analysis.observationId })}
            onClick={(event) => {
              // Use the analysis directly from the group instead of searching for it
              let foundAnalysis = analysis

              if (foundAnalysis && foundAnalysis.analysesPlanned) {
                foundAnalysis.tasks.sort((a, b) => DateTime.fromISO(a.end) - DateTime.fromISO(b.end))
              }

              const buttonRect = event.target.getBoundingClientRect();
              foundAnalysis.position = ({
                top: buttonRect.top + (1.5*buttonRect.height),
                left: buttonRect.left + (buttonRect.width / 2),
                height: buttonRect.height,
              })
              const baseName = group.baseSampleName || group.sampleName
              const customerName =
                foundAnalysis?.customerName ||
                foundAnalysis?.customer_name ||
                group.customerName ||
                group.customer_name ||
                null
              setActiveSampleAnalysis({ ...foundAnalysis, sampleName: baseName, productName: group.productName, customerName })
            }}
          >
            {analysis.shouldBeDoneToday && analysis.status === "DONE" ? (
              <FiCheck title={t("analysisAndSampleDone")} />
            ) : analysis.shouldBeDoneToday ? (
                <FiCheck title={t("analysisDone")} />
              ) : analysis.status === "DONE" ? (
                  <FiCheck title={t("sampleIsDone")} />
                ) : analysis.analysesPlanned ? (
                    <BsCalendar2 title={t("analysisPlanned")} />
                  ) : (
                      ""
                    )}
            <span style={{pointerEvents: 'none'}}>{analysis.name}</span>
            {analysis.shouldBeDone && (<FiAlertTriangle className="alert-triangle-analysis" title={t("sampleInProgressOnDoneAnalysis")} />)}
            {/* #4137: the status icons live in ONE corner cluster pinned above the name line, so
                they pack right-to-left by themselves. Fixed per-icon slots used to park a lone QA
                tick 2.9rem in from the edge — on top of the name — as if the flag and STOP icons
                were there too. */}
            <div className="samplerow-analysis-status">
              {analysis.isFlagged === true && (<FiFlag className="flag-analysis" title={t("analysisIsFlagged")} />)}
              {showLims && rejectedSet.has(String(analysis.sampleId)) && (
                <FiSlash className="batch-rejected-analysis" title={t("limsBatchRejectedIcon")} />
              )}
              {showLims && qaApprovedSet.has(String(analysis.sampleId)) && (
                <FiCheckCircle className="qa-approved-analysis" title={t("limsQaApprovedIcon")} />
              )}
            </div>
          </div>
        ))}
      </div>

      {showReleaseModal && (
        <ESignatureModal
          title={t("limsReleaseAction")}
          requiredCapability="qa"
          excludedMemberIds={releaseExcludedIds ? releaseExcludedIds.split(",") : []}
          commentContext="qa-release"
          onSign={handleRelease}
          onCancel={() => setShowReleaseModal(false)}
        />
      )}

      {showCoa && (
        <Popup title={t("limsCoaTitle")} onCancel={() => setShowCoa(false)}>
          <CertificateOfAnalysis group={group} />
        </Popup>
      )}

      {showEditPopup && (
        <Popup onCancel={() => { setShowEditPopup(false); setSelectedSampleForEdit(null) }}>
        <AddEditSamplePopup
          title={t("editSampleName", { name: initialSampleName })}
          initial={{
            sampleName: initialSampleName,
            analysisName: initialAnalysisIds, // Pass existing analyses so they can be filtered out
            observationId: initialObservationId,
            arrival: initialArrival,
            deadline: initialDeadline,
            priority: initialPriority,
            status: initialStatus,
            productName: initialProductName,
            exclusive: initialExclusive,
            isFlagged: initialIsFlagged,
            numberOfObservations: selected.numberOfObservations || undefined,
            // LIMS F-C1: the sample id so the popup can load/prefill this sample's QC details
            // (showLims-gated; harmless for the non-LIMS edit path).
            sampleId: selected.sampleId || selected.id || null,
          }}
          analysisCollection={analysisCollection}
          productCollection={productCollection}
          isEdit={true}
          onCancel={() => { setShowEditPopup(false); setSelectedSampleForEdit(null) }}
          onSubmit={(payload) => {
            const sampleId = selectedSampleForEdit ? (selectedSampleForEdit.sampleId || selectedSampleForEdit.id) : null
            if (!sampleId) {
              setShowEditPopup(false)
              setSelectedSampleForEdit(null)
              return
            }
            dispatch(sampleView.editSampleGroup(sampleId, payload)).then(() => {
              setShowEditPopup(false)
              setSelectedSampleForEdit(null)
            }).catch(() => {
              // keep popup open to let user retry
            })
          }}
        />
        </Popup>
      )}
    </div>
  )
}

// i18n key for a release status (limsRelease.RELEASE_STATUS value).
function releaseLabelKey(status) {
  switch (status) {
    case limsRelease.RELEASE_STATUS.RELEASED:
      return "limsReleaseStatusReleased"
    case limsRelease.RELEASE_STATUS.READY:
      return "limsReleaseStatusReady"
    case limsRelease.RELEASE_STATUS.ON_HOLD:
      return "limsReleaseStatusOnHold"
    case limsRelease.RELEASE_STATUS.BATCH_REJECTED:
      return "limsReleaseStatusBatchRejected"
    case limsRelease.RELEASE_STATUS.IN_REVIEW:
      return "limsReleaseStatusInReview"
    default:
      return "limsReleaseStatusInProgress"
  }
}
